/** @jsx jsx */
import React, { forwardRef, useCallback } from 'react';
import { css, jsx } from '@emotion/react';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { usePresetContext } from '../../presets/context';
import { ClickAreaMobile as ClickArea } from '../Addon';
import { createEditorContentStyle } from '../ContentStyles';
import WithFlash from '../WithFlash';

// Only mobile bridge is using this appearance. We have some plans to decouple the appearances from editor-core. So it doesn't make sense to move this plugin to a separated package for now.
// Copied packages/editor/editor-mobile-bridge/src/editor/editor-plugins/mobile-dimensions/index.ts

// Copied from packages/editor/editor-mobile-bridge/src/editor/editor-plugins/mobile-dimensions/types.ts

var mobileEditor = css({
  minHeight: '30px',
  width: '100%',
  maxWidth: 'inherit',
  boxSizing: 'border-box',
  wordWrap: 'break-word',
  'div > .ProseMirror': {
    outline: 'none',
    whiteSpace: 'pre-wrap',
    padding: 0,
    margin: 0
  }
});
var ContentArea = createEditorContentStyle();
ContentArea.displayName = 'ContentArea';
export var MobileAppearance = /*#__PURE__*/forwardRef(function MobileAppearance(_ref, ref) {
  var _api$base2;
  var editorView = _ref.editorView,
    persistScrollGutter = _ref.persistScrollGutter,
    children = _ref.children,
    editorDisabled = _ref.editorDisabled,
    featureFlags = _ref.featureFlags;
  var api = usePresetContext();
  var _useSharedPluginState = useSharedPluginState(api, ['maxContentSize', 'mobileDimensions']),
    maxContentSizeState = _useSharedPluginState.maxContentSizeState,
    mobileDimensionsState = _useSharedPluginState.mobileDimensionsState;
  var render = useCallback(function (_ref2) {
    var maxContentSize = _ref2.maxContentSize,
      mobileDimensions = _ref2.mobileDimensions;
    var maxContentSizeReached = Boolean(maxContentSize === null || maxContentSize === void 0 ? void 0 : maxContentSize.maxContentSizeReached);
    var minHeight = 100;
    var currentIsExpanded = true; // isExpanded prop should always be true for Hybrid Editor
    if (mobileDimensions) {
      var _api$base, _basePluginState$keyb;
      var windowHeight = mobileDimensions.windowHeight,
        mobilePaddingTop = mobileDimensions.mobilePaddingTop,
        isExpanded = mobileDimensions.isExpanded;
      var basePluginState = api === null || api === void 0 || (_api$base = api.base) === null || _api$base === void 0 ? void 0 : _api$base.sharedState.currentState();
      var keyboardHeight = (_basePluginState$keyb = basePluginState === null || basePluginState === void 0 ? void 0 : basePluginState.keyboardHeight) !== null && _basePluginState$keyb !== void 0 ? _basePluginState$keyb : -1;

      /*
        We calculate the min-height based on the windowHeight - keyboardHeight - paddingTop.
        This is needed due to scrolling issues when there is no content to scroll (like, only having 1 paragraph),
        but if the clickable area is bigger than the windowHeight - keyboard (including toolbar) then the view
        is scrolled nevertheless, and it gives the sensation that the content was lost.
      */

      if (!persistScrollGutter) {
        // in iOS Hybrid Editor windowHeight doesn't exclude keyboardHeight
        // in Android keyboardHeight is always set to -1;
        minHeight = windowHeight - keyboardHeight - 2 * mobilePaddingTop;
      } else {
        // in iOS Compact Editor windowHeight excludes keyboardHeight
        minHeight = windowHeight - mobilePaddingTop;
        // isExpanded can be true of false for Compact editor
        currentIsExpanded = isExpanded;
      }
    }
    return jsx(WithFlash, {
      animate: maxContentSizeReached
    }, jsx("div", {
      css: mobileEditor,
      ref: ref
    }, jsx(ClickArea, {
      editorView: editorView || undefined,
      minHeight: minHeight,
      persistScrollGutter: persistScrollGutter,
      isExpanded: currentIsExpanded,
      editorDisabled: editorDisabled
    }, jsx(ContentArea, {
      featureFlags: featureFlags
    }, jsx("div", {
      className: "ak-editor-content-area"
    }, children)))));
  }, [children, editorView, persistScrollGutter, editorDisabled, ref, featureFlags, api === null || api === void 0 || (_api$base2 = api.base) === null || _api$base2 === void 0 ? void 0 : _api$base2.sharedState]);
  return render({
    maxContentSize: maxContentSizeState,
    mobileDimensions: mobileDimensionsState
  });
});